// frontend/src/components/OwnerDashboard/FleetTable.js
// Replaces the stacked vehicle cards. Scrolls horizontally below 840px
// instead of crushing columns. Inline odometer edit with validation.

import { useState } from 'react';
import { IconCar, IconChevron, IconPencil } from '../icons/Icons';
import { fmt } from '../../theme/iparc';
import { oilStatus, badgesFor, hasIssue } from '../../hooks/useFleetHealth';

const COLS = '146px minmax(0,1.4fr) 112px minmax(0,1.5fr) 96px 78px';
const HEAD = ['Immatriculation', 'Véhicule', 'Odomètre', 'Prochaine vidange', 'Conformité', 'Action'];

export default function FleetTable({
  vehicles, health, onOpenVehicle, onSaveOdometer, onSeeAll, onAddVehicle, limit = 6,
}) {
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null); // { id, value, error }

  const shown = vehicles
    .filter(v => filter === 'all' ? true : filter === 'issue' ? hasIssue(v) : !hasIssue(v))
    .slice(0, limit);

  const chips = [
    { key: 'all',   label: `Tous · ${health.count}` },
    { key: 'issue', label: `En défaut · ${health.issueCount}` },
    { key: 'ok',    label: `Conformes · ${health.okCount}` },
  ];

  const save = async (v) => {
    const n = Number(String(editing.value).replace(/[^\d]/g, ''));
    if (!n || n < Number(v.odometerReading)) {
      setEditing(e => ({ ...e, error: `Minimum ${fmt(v.odometerReading)} km` }));
      return;
    }
    // Surface failures inline: without this a rejected request left the row
    // stuck in edit mode with no feedback (looks like "save did nothing").
    try {
      setEditing(e => ({ ...e, saving: true, error: null }));
      await onSaveOdometer?.(v, n);
      setEditing(null);
    } catch (err) {
      // vehicleRoutes replies { error: "..." } (not { message }) — e.g. the
      // "le kilométrage ne peut pas reculer" guard. Read both so the real
      // reason reaches the user instead of a generic failure.
      const data = err?.response?.data;
      setEditing(e => ({
        ...e,
        saving: false,
        error: data?.error || data?.message || 'Échec de l’enregistrement',
      }));
    }
  };

  return (
    <section className="rounded-2xl border border-[#241F1B] bg-gradient-to-b from-[#17140F] to-[#131110] overflow-x-auto overflow-y-hidden">
      <header className="flex flex-wrap items-center gap-3.5 px-[18px] py-[15px] border-b border-[#241F1B] min-w-[840px]">
        <h2 className="font-['Space_Grotesk',sans-serif] text-[14.5px] font-semibold tracking-[-.01em] text-[#F4F1EA] whitespace-nowrap">
          Ma flotte
        </h2>
        <div className="flex gap-[5px]">
          {chips.map(c => (
            <button key={c.key} onClick={() => setFilter(c.key)}
              className={`h-[26px] px-2.5 rounded-[7px] border border-[#2B2724] text-[11.5px] font-medium transition-colors ${
                filter === c.key ? 'bg-[#2B2724] text-[#F4F1EA]' : 'bg-transparent text-[#8F877B] hover:text-[#C9C3B6]'
              }`}>
              {c.label}
            </button>
          ))}
        </div>
        <button onClick={onAddVehicle}
          className="ml-auto text-[11.5px] text-[#8F877B] hover:text-[#F4F1EA] whitespace-nowrap transition-colors">
          Ajouter un véhicule
        </button>
      </header>

      <div className="grid gap-3.5 px-[18px] py-2.5 border-b border-[#241F1B] bg-[#121110] min-w-[840px]"
           style={{ gridTemplateColumns: COLS }}>
        {HEAD.map((h, i) => (
          <span key={h} className={`text-[9.5px] uppercase tracking-[.16em] text-[#5C554C] ${i === 2 || i === 5 ? 'text-right' : ''}`}>
            {h}
          </span>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="px-[18px] py-8 text-[12.5px] text-[#6E665C] min-w-[840px]">Aucun véhicule dans cette vue.</p>
      ) : shown.map((v) => {
        const oil = oilStatus(v);
        const isEditing = editing?.id === v._id;
        return (
          <div key={v._id}
               className="grid gap-3.5 px-[18px] py-3 border-b border-[#1E1B18] items-center min-w-[840px] hover:bg-[#1A1613] transition-colors"
               style={{ gridTemplateColumns: COLS }}>

            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-7 h-7 rounded-lg bg-[#241F1B] text-[#A79E90] flex items-center justify-center shrink-0">
                <IconCar className="w-[15px] h-[15px]" />
              </span>
              <span className="font-['Space_Grotesk',sans-serif] text-[13px] font-semibold tracking-[.02em] whitespace-nowrap text-[#F4F1EA]">
                {v.plateNumber}
              </span>
            </div>

            <div className="min-w-0">
              <p className="text-[12.5px] text-[#F4F1EA] truncate">{v.brand} {v.model}</p>
              <p className="text-[11px] text-[#6E665C] mt-0.5 truncate">{v.driverName || 'Non assigné'}</p>
            </div>

            <div className="text-right font-['Space_Grotesk',sans-serif] text-[13px] tabular-nums text-[#C9C3B6]">
              {fmt(v.odometerReading)} <span className="text-[10.5px] text-[#6E665C]">km</span>
            </div>

            <div className="min-w-0">
              {isEditing ? (
                <div>
                  <div className="flex gap-1.5 items-center">
                    <input
                      autoFocus value={editing.value}
                      onChange={(e) => setEditing({ ...editing, value: e.target.value, error: null })}
                      onKeyDown={(e) => { if (e.key === 'Enter') save(v); if (e.key === 'Escape') setEditing(null); }}
                      placeholder="Nouveau relevé"
                      className="flex-1 min-w-0 h-7 px-2.5 rounded-[7px] border border-[#4E4740] bg-[#100F0D]
                                 text-[#F4F1EA] font-['Space_Grotesk',sans-serif] text-xs outline-none"
                    />
                    <button onClick={() => save(v)} disabled={editing.saving}
                      className="h-7 px-2.5 rounded-[7px] bg-gradient-to-b from-[#F4F1EA] to-[#CFCAC0] text-[#141210] text-[11.5px] font-semibold disabled:opacity-50">
                      {editing.saving ? '…' : 'OK'}
                    </button>
                    <button onClick={() => setEditing(null)}
                      className="h-7 px-2 rounded-[7px] border border-[#2B2724] text-[#8F877B] text-[11.5px] hover:text-[#F4F1EA]">
                      ✕
                    </button>
                  </div>
                  {editing.error && <p className="text-[10.5px] text-[#E2574B] mt-1">{editing.error}</p>}
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-baseline mb-[5px]">
                    <span className="font-['Space_Grotesk',sans-serif] text-xs tabular-nums text-[#A79E90] whitespace-nowrap">
                      {fmt(v.nextOilChangeOdometer)} km
                    </span>
                    <span className="text-[10.5px] ml-2 truncate" style={{ color: oil.color }}>{oil.label}</span>
                  </div>
                  <span className="block h-1 rounded-[3px] bg-[#241F1B] overflow-hidden">
                    <span className="block h-full rounded-[3px]" style={{ width: oil.pct, background: oil.color }} />
                  </span>
                </div>
              )}
            </div>

            <div className="flex gap-1 flex-wrap">
              {badgesFor(v).map(b => (
                <span key={b.text} className="text-[10px] font-semibold tracking-[.04em] px-1.5 py-[2.5px] rounded-[5px] border"
                      style={{ color: b.color, background: b.tint, borderColor: b.tint }}>
                  {b.text}
                </span>
              ))}
            </div>

            <div className="flex justify-end gap-1.5">
              <button title="Mettre à jour l'odomètre"
                onClick={() => setEditing({ id: v._id, value: String(v.odometerReading ?? ''), error: null })}
                className="w-7 h-7 rounded-[7px] border border-[#2B2724] hover:border-[#4E4740] bg-[#141210]
                           text-[#8F877B] hover:text-[#F4F1EA] flex items-center justify-center transition-colors">
                <IconPencil className="w-3.5 h-3.5" />
              </button>
              <button title="Ouvrir la fiche" onClick={() => onOpenVehicle?.(v)}
                className="w-7 h-7 rounded-[7px] border border-[#2B2724] hover:border-[#4E4740] bg-[#141210]
                           text-[#8F877B] hover:text-[#F4F1EA] flex items-center justify-center transition-colors">
                <IconChevron className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        );
      })}

      {vehicles.length > limit && (
        <button onClick={onSeeAll}
          className="w-full py-3 text-[12px] text-[#8F877B] hover:text-[#F4F1EA] min-w-[840px] transition-colors">
          Voir les {vehicles.length} véhicules →
        </button>
      )}
    </section>
  );
}
